import React, { memo, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { areEqual } from 'Utils/equalityChecks';

import { UploadFileModal } from 'Components/Uploads';
import { projectIdSelector } from 'Containers/RocketScan/selectors';

import { uploadPDF } from 'Containers/Uploads/actions';
import { awsStore } from 'Utils/awsStore';

interface Props {
  isOpen: boolean;
  setIsOpen: (e: any) => void;
}

const postTransformObject = (response: any) => ({
  uuid: response.uuid,
  s3_key: response.key,
  bucket: response.bucket,
  file_name: `${response.name}${response.extension}`,
  name: response.name,
});

function UploadFileModalContainer({ isOpen, setIsOpen }: Props) {
  const dispatch = useDispatch();

  const projectId = useSelector(projectIdSelector, areEqual);

  const [fetching, setFetching] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [dragActive, setDragActive] = useState(false);

  const onFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    if (selected?.type === 'application/pdf') {
      setFile(selected);
    }
  }, []);

  const onDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
    const dropped = e.dataTransfer.files[0];
    if (dropped?.type === 'application/pdf') {
      setFile(dropped);
    }
  }, []);

  const onDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(true);
  }, []);

  const onDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
  }, []);

  const modalCloseClick = useCallback(() => {
    setIsOpen(false);
    setFetching(false);
    setFile(null);
    setDragActive(false);
  }, []);

  const onUploadComplete = useCallback(() => {
    setIsOpen(false);
    setFile(null);
    setFetching(false);
  }, []);

  const uploadButtonClick = useCallback(async () => {
    if (!file) return;

    setFetching(true);

    try {
      const response = await awsStore(file, '');
      const transformed = postTransformObject(response);
      dispatch(uploadPDF(projectId, transformed, onUploadComplete));
    } catch (error: any) {
      setFetching(false);

      if (error?.response?.status === 419) {
        // eslint-disable-next-line no-alert
        alert('Your session has expired. Please refresh the page and try again.');
      } else {
        // eslint-disable-next-line no-alert
        alert('Upload failed. Please try again.');
      }
    }
  }, [file, projectId]);

  return (
    <UploadFileModal
      isOpen={isOpen}
      fetching={fetching}
      file={file}
      dragActive={dragActive}
      modalCloseClick={modalCloseClick}
      uploadButtonClick={uploadButtonClick}
      onFileSelect={onFileSelect}
      onDrop={onDrop}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
    />
  );
}

const UploadFileModalContainerMemo = memo(UploadFileModalContainer, areEqual);

export { UploadFileModalContainerMemo as UploadFileModal };
